import styles from './WhereField.less';

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { Autosuggest } from 'seek-style-guide/react';
import { parse as parseAutosuggestMatches } from 'autosuggest-highlight';
import { correctBackdropPosition } from '../SearchBar';

import {
  whereFieldFocused,
  whereFieldTyped,
  updateWhereField,
  fetchWhereSuggestions,
  clearWhereSuggestions,
  getPersonalisedLocationSuggestions
} from 'src/store/search/search';

function mapStateToProps({ search, location }) {
  return {
    value: search.whereField,
    suggestions: search.whereSuggestions,
    locale: location.locale
  };
}

const mapDispatchToProps = {
  whereFieldFocused,
  whereFieldTyped,
  updateWhereField,
  fetchWhereSuggestions,
  clearWhereSuggestions,
  getPersonalisedLocationSuggestions
};

class WhereField extends Component {
  static propTypes = {
    value: PropTypes.string,
    suggestions: PropTypes.array.isRequired,
    locale: PropTypes.string.isRequired,
    whereFieldFocused: PropTypes.func.isRequired,
    whereFieldTyped: PropTypes.func.isRequired,
    updateWhereField: PropTypes.func.isRequired,
    fetchWhereSuggestions: PropTypes.func.isRequired,
    clearWhereSuggestions: PropTypes.func.isRequired,
    getPersonalisedLocationSuggestions: PropTypes.func.isRequired,

    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    labelProps: PropTypes.object,
    onSuggestionSelected: PropTypes.func.isRequired,
    onInputBlur: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired
  };

  static defaultProps = {
    value: '',
    labelProps: {}
  };

  componentDidMount() {
    window.addEventListener('resize', this.resizeHandler);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeHandler);
  }

  resizeHandler = () => correctBackdropPosition(this.autoSuggestBackdropEl);

  storeInputReference = autosuggest => {
    if (autosuggest !== null) {
      this.input = autosuggest.input;
    }
  };

  onSuggestionSelected = (event, { suggestion, suggestionValue }) => {
    event.preventDefault();
    this.props.onSuggestionSelected({ suggestion, suggestionValue });
  };

  renderSuggestion = suggestion => {
    return (
      <span>
        {suggestion.displayParts
          ? suggestion.displayParts
              .map(({ text, matches }) =>
                parseAutosuggestMatches(text, matches)
              )
              .map((highlightRules, partIndex) => (
                <span key={partIndex} className={styles.suggestionPart}>
                  {highlightRules.map(({ text, highlight }, ruleIndex) => (
                    <span
                      key={ruleIndex}
                      className={classnames({
                        [styles.suggestionPartMatch]: highlight
                      })}
                    >
                      {text}
                    </span>
                  ))}
                  &nbsp;
                </span>
              ))
          : suggestion.displayText}
      </span>
    );
  };

  getSuggestionValue = suggestion => suggestion.displayText;

  shouldRenderSuggestions = () => true;

  onChange = (event, { newValue }) => {
    this.props.updateWhereField(newValue);
  };

  onClear = () => {
    this.props.updateWhereField('');
    this.props.onClear();
    this.props.getPersonalisedLocationSuggestions();
  };

  onSuggestionsFetchRequested = ({ value }) => {
    if (value.trim() === '') {
      this.props.getPersonalisedLocationSuggestions();
    } else {
      const { locale } = this.props;

      this.props.fetchWhereSuggestions({ value, locale });
    }
  };

  onSuggestionsClearRequested = () => {
    this.props.clearWhereSuggestions();
  };

  renderSectionTitle = section => {
    return section.sectionName;
  };

  getSectionSuggestions = section => {
    return section.suggestions;
  };

  setRef = ref => {
    if (ref) {
      this.autoSuggestBackdropEl = ref.querySelector('div > div').lastChild;
      correctBackdropPosition(this.autoSuggestBackdropEl);
    }
  };

  render() {
    const {
      value,
      suggestions,
      locale,
      id,
      name,
      label,
      labelProps,
      onInputBlur: onBlur,
      whereFieldFocused: onFocus,
      whereFieldTyped: onKeyDown
    } = this.props;
    const inputProps = {
      name,
      className: styles.input,
      spellCheck: 'false',
      'data-automation': id,
      placeholder:
        locale.toLowerCase() === 'nz'
          ? 'Enter suburb, town or city'
          : 'Enter suburb, city, or region',
      onKeyDown
    };

    return (
      <div ref={this.setRef} data-automation="whereFieldOptions">
        <Autosuggest
          ref={this.storeInputReference}
          className={styles.root}
          inputProps={inputProps}
          onClear={this.onClear}
          value={value}
          type="search"
          onFocus={onFocus}
          onChange={this.onChange}
          onBlur={onBlur}
          id={id}
          label={label}
          showMobileBackdrop
          labelProps={{
            ...labelProps,
            ref: whereLabel => {
              this.whereLabel = whereLabel;
            }
          }}
          message={false}
          autosuggestProps={{
            multiSection: true,
            onSuggestionsFetchRequested: this.onSuggestionsFetchRequested,
            onSuggestionsClearRequested: this.onSuggestionsClearRequested,
            getSuggestionValue: this.getSuggestionValue,
            renderSuggestion: this.renderSuggestion,
            onSuggestionSelected: this.onSuggestionSelected,
            shouldRenderSuggestions: this.shouldRenderSuggestions,
            renderSectionTitle: this.renderSectionTitle,
            getSectionSuggestions: this.getSectionSuggestions,
            suggestions
          }}
          suggestionsContainerClassName={styles.suggestionsContainer}
          data-automation="whereFieldAutoSuggest"
        />
      </div>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(WhereField);
